import { Identifier } from '@app/utils/common';
import { StringKeys } from '@app/utils/string-keys';
import { compare, SortOrder } from './compare';

/**
 * Returns a sort function that can be used to sort an array of
 * objects. This is a newer method signature of {@link sortBy}
 * because {@link sortBy} has to be backwards compatible.
 *
 * @param orderings List of sort order definitions that describe the desired sorting.
 * Can be in the form of:
 *  - an array: ["key" | ((item: T) => number | string), "direction" | (sort object without key)]
 *  - an object: {
 *      key: string | ((item: T) => number | string),
 *      direction: "asc|desc",
 *      ignoreCase?: boolean,
 *      defaultValue: boolean
 *    }
 */
export function sortedBy<T>(...orderings: SortOrder<T>[]): (a: T, b: T) => number {
  return sortBy(orderings);
}

/**
 * Returns a sort function that can be used to sort an array of objects.
 *
 * @param orderings List of sort order definitions that describe the desired sorting.
 * Can be in the form of:
 *  - an array: ["key" | ((item: T) => number | string), "direction"]
 *  - an object: {
 *      key: string | ((item: T) => number | string),
 *      direction: "asc|desc",
 *      ignoreCase?: boolean,
 *      defaultValue: boolean
 *    }
 */
export function sortBy<T>(orderings: SortOrder<T>[]): (a: T, b: T) => number;

/**
 * Returns a sort function that can be used to sort an array of objects by the given key.
 *
 * @param key Sort by this key (can be a path).
 * @param reverse Sort in reverse.
 * @param defaultValue If the key isn't found on an object then this defaultValue will be used.
 * @param ignoreCase If the values are strings then the case will be ignored.
 * @deprecated Use either the override `sortBy([...orderings])` or {@link sortedBy}.
 */
export function sortBy<T>(
  key: StringKeys<T> | string | string[] | ((item: T) => Identifier),
  reverse?: boolean,
  defaultValue?: T,
  ignoreCase?: boolean
): (a: T, b: T) => number;

/**
 * Returns a sort function that can be used to sort an array of objects by the given key.
 *
 * @param key Sort by this key (can be a path).
 * @param reverse Sort in reverse.
 * @param defaultValue If the key isn't found on an object then this defaultValue will be used.
 * @param ignoreCase If the values are strings then the case will be ignored.
 */
export function sortBy<T>(
  key: StringKeys<T> | string | ((item: T) => Identifier) | (string | SortOrder<T>)[],
  reverse: boolean = false,
  defaultValue?: any,
  ignoreCase: boolean = false
) {
  let compareBy: SortOrder<T>[];

  if (typeof key === 'string') {
    compareBy = [
      {
        defaultValue,
        ignoreCase,
        key,
        order: reverse ? 'desc' : 'asc',
      },
    ];
  } else if (Array.isArray(key)) {
    compareBy = key.map((key) => {
      if (typeof key === 'string') {
        return {
          key,
          order: reverse ? 'desc' : 'asc',
        };
      } else {
        return key;
      }
    });
  } else {
    compareBy = [
      {
        defaultValue,
        ignoreCase,
        key,
        order: reverse ? 'desc' : 'asc',
      },
    ];
  }

  return (a: T, b: T) => {
    let sorted = 0;

    if (Array.isArray(compareBy)) {
      for (const key of compareBy) {
        sorted = compare(a, b, key);

        // Stop comparing if a and b differ using the current property.
        if (sorted !== 0) {
          break;
        }
      }
    } else {
      sorted = compare(a, b, compareBy);
    }

    return sorted;
  };
}
